import { createContext, useContext, useState, type ReactNode } from 'react';
import type { DashboardView, PeriodSpec, ScopeFilters } from '@contracts';
import { getCompanies, isSuperAdmin } from '@/services/authService';

/**
 * Filtros GLOBALES del dashboard (aplican a todos los widgets). El builder de
 * widgets (fase posterior) permitirá override por widget; por ahora, uno global.
 */
interface FilterState {
  period: PeriodSpec;
  companies: string[]; // pos_slug[]; [] = todas las del usuario
  scope: ScopeFilters; // Sucursal (branches) + Equipo (teams) por ahora
  view: DashboardView; // 'ventas' (por orden) | 'vendedor' (por comisión)
  setPeriod: (p: PeriodSpec) => void;
  setCompanies: (c: string[]) => void;
  setScope: (s: ScopeFilters) => void;
  setView: (v: DashboardView) => void;
}

const FilterContext = createContext<FilterState | null>(null);

export function FilterProvider({ children }: { children: ReactNode }) {
  const [period, setPeriod] = useState<PeriodSpec>('today');
  // Super admin arranca con las 3 marcas (incluye Smart Bamboo/Metabase); el resto
  // solo con sus empresas del JWT (evita pedir empresas sin acceso al cargar). El
  // scope real lo impone el BFF. Ver CompanySelect.
  const [companies, setCompanies] = useState<string[]>(
    isSuperAdmin() ? ['vg', 'pi', 'sb'] : getCompanies().map((c) => c.slug),
  );
  const [scope, setScope] = useState<ScopeFilters>({});
  const [view, setView] = useState<DashboardView>('ventas');
  return (
    <FilterContext.Provider
      value={{ period, companies, scope, view, setPeriod, setCompanies, setScope, setView }}
    >
      {children}
    </FilterContext.Provider>
  );
}

export function useFilters(): FilterState {
  const ctx = useContext(FilterContext);
  if (!ctx) throw new Error('useFilters fuera de FilterProvider');
  return ctx;
}
